/**
 * Reach model for the Audience Builder step.
 *
 * A single pure computation turns the filter selections (and any sites
 * uploaded in Step 2) into every number the UI renders: the headline reach,
 * the filter funnel, the gender split, per-channel reachability and the
 * market heat map. Keeping it in one place means the summary bar, funnel and
 * map can never disagree.
 */

import {
  GRAPH_BASE_REACH,
  CITY_SHARE,
  AGE_OPTIONS,
  AGE_SHARES,
  DEVICE_OPTIONS,
  DEVICE_SHARES,
  DEVICE_TIER_OPTIONS,
  OS_OPTIONS,
  OS_SHARES,
  TIER_SHARES,
} from './constants';
import type {
  ChannelReach,
  FilterSelections,
  FunnelStage,
  GenderSplit,
  Market,
  ReachModel,
  ReachModelInput,
} from './types';

// Formatting
/** Format a raw reach count in Indian units (K / L / Cr). */
export const formatReach = (value: number): string => {
  if (value >= 1e7) {
    const crores = value / 1e7;
    return `${crores >= 10 ? Math.round(crores) : crores.toFixed(1)}Cr`;
  }
  if (value >= 1e5) return `${(value / 1e5).toFixed(2)}L`;
  if (value >= 1e3) return `${Math.round(value / 1e3)}K`;
  return `${Math.max(0, Math.round(value))}`;
};

// Filter factors
/** No selection = no filter (factor 1); otherwise sum of shares, capped at 1. */
const sumShares = (
  selected: string[],
  shares: Record<string, number>
): number =>
  selected.length === 0
    ? 1
    : Math.min(
        1,
        selected.reduce((sum, key) => sum + (shares[key] ?? 0.05), 0)
      );

/** Which single gender the selection narrows to, if any. */
const genderOnly = (genders: string[]): 'female' | 'male' | null => {
  const lower = genders.map(g => g.toLowerCase());
  if (lower.includes('all')) return null;
  const female = lower.includes('female');
  const male = lower.includes('male');
  if (female && !male) return 'female';
  if (male && !female) return 'male';
  return null;
};

const genderFactor = (genders: string[]): number => {
  const only = genderOnly(genders);
  return only === 'female' ? 0.48 : only === 'male' ? 0.52 : 1;
};

// Selections
/**
 * Everything unconstrained to begin with: demographics, device and OS start
 * fully selected (which multiplies out to factor 1, same as no selection) so
 * the panel opens showing the full city audience rather than a blank slate.
 * Interests, affinities and exclusions stay empty - those are deliberate
 * narrowing choices, not defaults.
 */
export const DEFAULT_SELECTIONS: FilterSelections = {
  genders: ['all'],
  ages: [...AGE_OPTIONS],
  devices: [...DEVICE_OPTIONS],
  os: [...OS_OPTIONS],
  deviceTiers: [...DEVICE_TIER_OPTIONS],
  interests: [],
  affinities: [],
  exclusions: [],
};

// Reference metros (used to name/anchor markets)
interface City {
  name: string;
  lat: number;
  lng: number;
  /** Rough relative audience weight, so default markets aren't uniform. */
  weight: number;
}

const CITIES: City[] = [
  { name: 'Mumbai', lat: 19.076, lng: 72.8777, weight: 1.0 },
  { name: 'Delhi NCR', lat: 28.6139, lng: 77.209, weight: 0.96 },
  { name: 'Bangalore', lat: 12.9716, lng: 77.5946, weight: 0.82 },
  { name: 'Hyderabad', lat: 17.385, lng: 78.4867, weight: 0.68 },
  { name: 'Chennai', lat: 13.0827, lng: 80.2707, weight: 0.6 },
  { name: 'Kolkata', lat: 22.5726, lng: 88.3639, weight: 0.58 },
  { name: 'Pune', lat: 18.5204, lng: 73.8567, weight: 0.5 },
  { name: 'Ahmedabad', lat: 23.0225, lng: 72.5714, weight: 0.44 },
];

/** Nearest reference city to an arbitrary point (planar distance is fine at metro scale). */
export const nearestCity = (lat: number, lng: number): City =>
  CITIES.reduce((best, city) => {
    const d = (city.lat - lat) ** 2 + (city.lng - lng) ** 2;
    const bestD = (best.lat - lat) ** 2 + (best.lng - lng) ** 2;
    return d < bestD ? city : best;
  }, CITIES[0]);

// Channel shares of deliverable reach
/**
 * Share of deliverable reach each channel can be served on. DOOH used to hold
 * 0.34 of the 1.80 total; it's the audience source rather than a delivery
 * channel, so that share was folded back into the remaining three in
 * proportion to their own (×1.23) instead of being dropped - the totals the
 * summary bar and Step 4 quote stay the size they were.
 */
const CHANNEL_SHARES: { label: string; share: number }[] = [
  { label: 'Mobile', share: 0.96 },
  { label: 'Web', share: 0.64 },
  { label: 'CTV', share: 0.2 },
];

// Computation
export const computeReachModel = ({
  selections,
  sites,
}: ReachModelInput): ReachModel => {
  const universe = GRAPH_BASE_REACH;

  // Per-market base slice of the national universe.
  const base = universe * CITY_SHARE;

  // Progressive filter factors.
  const genderF = genderFactor(selections.genders);
  const ageF = sumShares(selections.ages, AGE_SHARES);
  const deviceF =
    sumShares(selections.devices, DEVICE_SHARES) *
    sumShares(selections.os, OS_SHARES) *
    sumShares(selections.deviceTiers, TIER_SHARES);
  const interestF =
    selections.interests.length > 0
      ? 1 - Math.pow(0.6, selections.interests.length)
      : 1;
  const affinityF =
    selections.affinities.length > 0
      ? 1 - Math.pow(0.7, selections.affinities.length)
      : 1;
  const exclusionF = Math.pow(0.94, selections.exclusions.length);

  const afterDemo = base * genderF * ageF;
  const afterDevice = afterDemo * deviceF;
  const afterInterest = afterDevice * interestF * affinityF;
  const addressable = Math.max(0, Math.round(afterInterest * exclusionF));

  const funnel: FunnelStage[] = [
    { label: 'Universe', value: Math.round(base) },
    { label: 'Demographics', value: Math.round(afterDemo) },
    { label: 'Device', value: Math.round(afterDevice) },
    { label: 'Interests', value: Math.round(afterInterest) },
    { label: 'Addressable', value: addressable },
  ];

  // Gender split
  // Narrowing to one gender means the deliverable audience *is* that gender:
  // 100 / 0, not a lean. Unconstrained falls back to the graph's own mix.
  const only = genderOnly(selections.genders);
  const genderSplit: GenderSplit =
    only === 'female'
      ? { female: 100, male: 0 }
      : only === 'male'
        ? { female: 0, male: 100 }
        : { female: 51, male: 49 };

  // Markets
  const hasSites = sites.length > 0;
  let markets: Market[];

  if (hasSites) {
    // Cluster uploaded sites onto their nearest metro.
    const clusters = new Map<string, { city: City; count: number }>();
    sites.forEach(site => {
      const city = nearestCity(site.lat, site.lng);
      const entry = clusters.get(city.name);
      if (entry) {
        entry.count += 1;
      } else {
        clusters.set(city.name, { city, count: 1 });
      }
    });
    const maxCount = Math.max(...[...clusters.values()].map(c => c.count));
    markets = [...clusters.values()]
      .map(({ city, count }) => ({
        id: city.name,
        name: city.name,
        lat: city.lat,
        lng: city.lng,
        // Sites in the same city overlap heavily, so each extra one adds less
        // incremental reach and the market can never exceed its own audience.
        reach: Math.round(addressable * (1 - Math.pow(0.72, count))),
        intensity: maxCount > 0 ? count / maxCount : 1,
      }))
      .sort((a, b) => b.reach - a.reach);
  } else {
    // No sites yet - preview the top metros at their per-market audience.
    const maxWeight = Math.max(...CITIES.map(c => c.weight));
    markets = CITIES.map(city => ({
      id: city.name,
      name: city.name,
      lat: city.lat,
      lng: city.lng,
      reach: Math.round(addressable * city.weight),
      intensity: city.weight / maxWeight,
    })).sort((a, b) => b.reach - a.reach);
  }

  const siteCount = sites.length;
  // Total = the sum of what each market actually contributes (de-duplicated
  // above), so uploading 200 sites in 3 cities can't inflate reach to crores.
  const total = hasSites
    ? markets.reduce((sum, market) => sum + market.reach, 0)
    : addressable;

  const channels: ChannelReach[] = CHANNEL_SHARES.map(c => ({
    label: c.label,
    active: hasSites,
    value: Math.round(total * c.share),
  }));

  return {
    universe,
    cityUniverse: Math.round(base),
    addressable,
    total,
    hasSites,
    siteCount,
    funnel,
    genderSplit,
    channels,
    markets,
  };
};
